import React, { useState } from 'react';
import BusinessKitzLogo from '../../../assets/svg/business-kitz-logo.svg?react';
import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined';
import CottageOutlinedIcon from '@mui/icons-material/CottageOutlined';
import LinkItem from './components/LinkItem/LInkItem';
import { useTranslation } from 'react-i18next';
import Tooltip from '@mui/material/Tooltip';
import { toast } from 'react-toastify';
import { useHasClerkRole } from '../hooks/useHasClerkRole';
import { useDocumensoRedirect } from '../hooks/useDocumensoRedirect';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import PackageIcon from '../../../assets/svg/menu/package-2.svg?react';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { ROUTES } from '../constants/routes';
import { useLocation } from 'react-router-dom';
import { useClerk } from '@clerk/clerk-react';
import { selectAllCompanies } from '../../slices/companies/companiesSlice';
import { useAppSelector } from '../hooks/useAppSelector';
import { PermissionsGate, RolesGate } from '../roles-gate';
import { ROLES } from '../constants/groups';
import { selectUser } from '../../slices/user/userSlice';
import { selectAllEmployees } from '../../slices/employees/employeesSlice';
import { revertAll } from '../../store';
import { useAppDispatch } from '../hooks/useAppDispatch';
import CalendarIcon from '../../../assets/svg/calendar_month.svg?react';
import ContractEditIcon from '../../../assets/svg/menu/contract-edit.svg?react';
import LogoutIcon from '../../../assets/svg/menu/logout.svg?react';
import BusinessCenterIcon from '../../../assets/svg/menu/business-center.svg?react';
import { USER_PERMISSIONS } from '../constants/permissions';
import { Button } from '@mui/material';

const MainNavigation = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { signOut } = useClerk();
  const path = location.pathname;
  const company = useAppSelector(selectAllCompanies)[0];
  const currentUser = useAppSelector(selectUser);
  const userIsEmployee = useHasClerkRole('org:employee');
  const employees = useAppSelector(selectAllEmployees);
  const employeeId =
    userIsEmployee &&
    employees.find(
      (employee) =>
        employee.email_address.toLowerCase() === currentUser.email ||
        employee.business_email_address.toLowerCase() === currentUser.email
    )?.id;
  const { redirectToDocumenso } = useDocumensoRedirect();
  const [expandedView, setExpandedView] = useState<boolean>(false);
  return (
    <>
      {expandedView && (
        <div
          tabIndex={0}
          role='button'
          className='navigation-under-layer'
          onClick={() => setExpandedView(false)}
          onKeyDown={() => {
            return;
          }}
        />
      )}
      <nav className={'main-navigation' + (expandedView ? ' expanded-view' : '')}>
        {expandedView ? (
          <div className='logo-wrapper'>
            <BusinessKitzLogo />
          </div>
        ) : (
          <div
            role='button'
            tabIndex={0}
            className='menu-control-wrapper'
            onClick={() => setExpandedView(true)}
            onKeyDown={() => {
              return;
            }}
          >
            <MenuOutlinedIcon fontSize='large' />
          </div>
        )}
        <ul className='list-unstyled'>
          <li>
            <LinkItem
              url={ROUTES.HOME}
              active={path === ROUTES.HOME}
              expandedView={expandedView}
              linkText={t('menu.home')}
              onClick={() => setExpandedView(false)}
            >
              <CottageOutlinedIcon fontSize='medium' />
            </LinkItem>
          </li>
        </ul>
        {!userIsEmployee && <div className='section-label'>{t('menu.documents')}</div>}
        <ul className='list-unstyled with-border'>
          <PermissionsGate
            permission={[
              USER_PERMISSIONS.ORG_DOCUMENT_LIBRARY_READ,
              USER_PERMISSIONS.ORG_DOCUMENT_TEMPLATE_READ,
            ]}
          >
            <li>
              <LinkItem
                url={ROUTES.DOCUMENT_LIBRARY}
                active={path.includes(ROUTES.DOCUMENT_LIBRARY)}
                expandedView={expandedView}
                linkText={t('menu.library')}
                onClick={() => setExpandedView(false)}
              >
                <AccountBalanceOutlinedIcon fontSize='medium' />
              </LinkItem>
            </li>
          </PermissionsGate>
          <RolesGate role={ROLES.ADMIN}>
            <li className='key-dates-link'>
              <LinkItem
                url={ROUTES.DOCUMENTS_KEY_DATES}
                active={path.includes(ROUTES.DOCUMENTS_KEY_DATES)}
                expandedView={expandedView}
                linkText={t('menu.keyDates')}
                onClick={() => setExpandedView(false)}
              >
                <CalendarIcon />
              </LinkItem>
            </li>
          </RolesGate>
          {/*<li>
            <LinkItem
              url={ROUTES.WORKSPACE}
              active={path.includes(ROUTES.WORKSPACE)}
              onClick={() => setExpandedView(false)}
              expandedView={expandedView}
              linkText={t('menu.workspace')}
            >
              <ClarifyIcon />
            </LinkItem>
          </li>*/}
          <RolesGate role={ROLES.ADMIN}>
            <li>
              <LinkItem
                url={ROUTES.DOCUMENT_VAULT}
                active={path.includes(ROUTES.DOCUMENT_VAULT)}
                expandedView={expandedView}
                linkText={t('menu.vault')}
                onClick={() => setExpandedView(false)}
              >
                <PackageIcon />
              </LinkItem>
            </li>
          </RolesGate>
          {company && (
            <PermissionsGate permission={USER_PERMISSIONS.ORG_DOCUMENT_SIGN_READ}>
              <li>
                {expandedView ? (
                  <Button
                    className={'main-nav-link' + ' active'}
                    onClick={() => {
                      setExpandedView(false);
                      redirectToDocumenso('/documents').catch(() => {
                        toast.error('Failed to redirect to Documenso');
                      });
                    }}
                  >
                    <ContractEditIcon />
                    <span className='main-nav-link-text'>{t('menu.sign')}</span>
                  </Button>
                ) : (
                  <Tooltip title={t('menu.sign')} placement='top' arrow>
                    <Button
                      className='main-nav-link'
                      onClick={() => {
                        setExpandedView(false);
                        redirectToDocumenso('/documents').catch(() => {
                          toast.error('Failed to redirect to Documenso');
                        });
                      }}
                    >
                      <ContractEditIcon />
                    </Button>
                  </Tooltip>
                )}
              </li>
            </PermissionsGate>
          )}
        </ul>
        {!userIsEmployee && <div className='section-label'>{t('menu.profiles')}</div>}
        <ul className='list-unstyled'>
          <PermissionsGate permission={USER_PERMISSIONS.ORG_BUSINESS_PROFILE_READ}>
            <li>
              <LinkItem
                url={ROUTES.BUSINESS_PROFILE}
                active={path.includes(ROUTES.BUSINESS_PROFILE)}
                expandedView={expandedView}
                linkText={t('menu.business')}
                onClick={() => setExpandedView(false)}
              >
                <ApartmentOutlinedIcon fontSize='medium' />
              </LinkItem>
            </li>
          </PermissionsGate>
          <PermissionsGate permission={USER_PERMISSIONS.ORG_EMPLOYEE_PROFILE_READ}>
            <li>
              <LinkItem
                url={
                  userIsEmployee && employeeId
                    ? `${ROUTES.EMPLOYEE_PROFILES}/${employeeId}`
                    : ROUTES.EMPLOYEE_PROFILES
                }
                active={path.includes(ROUTES.EMPLOYEE_PROFILES)}
                expandedView={expandedView}
                linkText={userIsEmployee ? t('buttons.employee') : t('menu.employees')}
                onClick={() => setExpandedView(false)}
              >
                <BadgeOutlinedIcon fontSize='medium' />
              </LinkItem>
            </li>
          </PermissionsGate>
          <PermissionsGate permission={USER_PERMISSIONS.ORG_CUSTOMER_PROFILE_READ}>
            <li>
              <LinkItem
                url={ROUTES.CUSTOMER_PROFILES}
                active={path.includes(ROUTES.CUSTOMER_PROFILES)}
                expandedView={expandedView}
                linkText={t('menu.customers')}
                onClick={() => setExpandedView(false)}
              >
                <SupportAgentOutlinedIcon fontSize='medium' />
              </LinkItem>
            </li>
          </PermissionsGate>
          <PermissionsGate permission={USER_PERMISSIONS.ORG_SUPPLIER_PROFILE_READ}>
            <li>
              <LinkItem
                url={ROUTES.SUPPLIER_PROFILES}
                active={path.includes(ROUTES.SUPPLIER_PROFILES)}
                expandedView={expandedView}
                linkText={t('menu.suppliers')}
                onClick={() => setExpandedView(false)}
              >
                <BusinessCenterIcon />
              </LinkItem>
            </li>
          </PermissionsGate>
        </ul>
        <PermissionsGate permission={USER_PERMISSIONS.ORG_SUBSCRIPTION_MANAGE}>
          <ul className='list-unstyled mt-auto with-border'>
            <li>
              <LinkItem
                url={ROUTES.PRICING}
                active={path.includes(ROUTES.PRICING)}
                expandedView={expandedView}
                linkText={t('menu.plans')}
                onClick={() => setExpandedView(false)}
              >
                <SellOutlinedIcon fontSize='medium' />
              </LinkItem>
            </li>
          </ul>
        </PermissionsGate>
        <ul className='list-unstyled'>
          <PermissionsGate permission={USER_PERMISSIONS.ORG_MEMBERSHIP_VIEW}>
            <li>
              <LinkItem
                url={ROUTES.SETTINGS}
                active={path.includes(ROUTES.SETTINGS)}
                expandedView={expandedView}
                linkText={t('menu.settings')}
                onClick={() => setExpandedView(false)}
              >
                <SettingsOutlinedIcon fontSize='medium' />
              </LinkItem>
            </li>
          </PermissionsGate>
          <li>
            {expandedView ? (
              <span
                className='main-nav-link'
                role='button'
                tabIndex={-1}
                onKeyUp={() => {
                  return;
                }}
                onClick={() => signOut({ redirectUrl: ROUTES.LOGIN })}
              >
                <LogoutIcon />
                <span className='main-nav-link-text'>{t('menu.logout')}</span>
              </span>
            ) : (
              <Tooltip title={t('menu.logout')} placement='top' arrow>
                <span
                  className='main-nav-link'
                  role='button'
                  tabIndex={-1}
                  onKeyUp={() => {
                    return;
                  }}
                  onClick={() => {
                    signOut({ redirectUrl: ROUTES.LOGIN }).catch();
                    dispatch(revertAll());
                  }}
                >
                  <LogoutIcon />
                </span>
              </Tooltip>
            )}
          </li>
        </ul>
      </nav>
    </>
  );
};

export default MainNavigation;
